import { createAsyncThunk, createSlice, PayloadAction } from "@reduxjs/toolkit";
import {
  fetchExploreMarketProducts,
  requestExploreMarketProductAgreement,
  SearchAgreementRequestsRequest,
  SearchAgreementRequestsPage,
  searchExploreMarketAgreementRequests,
  fetchRequestedProductIds,
  updateAgreementRequestStatus as updateAgreementRequestStatusApi,
  UpdateAgreementRequestStatusRequest,
} from "../../../service/ExploreMarketService";
import { LoadingType } from "../../../types/CommonTypes";
import { useAppSelector } from "../redux-hooks";
import { ProductConfigRules } from "../../../service/ProductService";

export interface MarketProduct {
  productId: number;
  productName: string;
  hcpcsCode: string | null;
  productCategory: string | null;
  composition: string | null;
  /** CS-2073 — sterile | non-sterile | null */
  compoundType?: string | null;
  allowableRate: number | null;
  listPrice?: number | null;
  mues: string | null;
  manufacturerId: number | null;
  manufacturerName: string | null;
  isAgreementRequired: boolean | null;
  agreementStatus: string | null;
  // CS-1760 - Raw agreement mode from product config rule
  agreementMode: string | null;
  productLogoId: number | null;
  // UI-only
  isFavorited: boolean;
  isAgreementRequestedByUser?: boolean;
  productStatus: string | null; // ACTIVE, IN PROGRESS, INACTIVE, TBD
  comingSoon?: boolean;
  skuAllowablePrices?: any[];
  macPrices?: any[];
  extendedPrice?:number|null;
  productDescription?: string | null;
  hasSKUPrice?: boolean; // CS-1732
  purchaseAndBill?: string | null; // CS-1765
  productConfigs?: ProductConfigRules[];
}

export type ExploreMarketDto = {
  marketData: MarketProduct[];
  fetchStatus: LoadingType;
  requestAgreementLoading?: boolean;
  requestAgreementError?: string | null;
  searchAgreementRequestStatus: LoadingType;
  searchAgreementRequestError?: string | null;
  searchAgreementRequestList: SearchAgreementRequestsPage;
  requestedProductIds: number[];
};

const initialState: ExploreMarketDto = {
  marketData: [],
  fetchStatus: "idle",
  requestAgreementLoading: false,
  requestAgreementError: null,
  searchAgreementRequestStatus: "idle",
  searchAgreementRequestError: null,
  searchAgreementRequestList: {
    content: [],
    totalElements: 0,
    totalPages: 0,
    size: 0,
  },
  requestedProductIds: [],
};

export const getExploreMarketProducts = createAsyncThunk(
  "exploreMarket/getExploreMarketProducts",
  async (myProducts: boolean = false, { rejectWithValue }) => {
    try {
      const response = await fetchExploreMarketProducts(myProducts);
      return response.data;
    } catch (error) {
      return rejectWithValue(error || "An error occurred while fetching explore market products");
    }
  }
);

// CS-1741: Search agreement requests for practice in explore market
export const searchAgreementRequests = createAsyncThunk(
  "exploreMarket/searchAgreementRequests",
  async (request: SearchAgreementRequestsRequest, { rejectWithValue }) => {
    try {
      const response = await searchExploreMarketAgreementRequests(request);
      return response.data;
    } catch (error) {
      return rejectWithValue(error || "An error occurred while searching agreement requests");
    }
  }
);

// CS-1731: Request product agreement thunk
export const requestProductAgreement = createAsyncThunk(
  "exploreMarket/requestProductAgreement",
  async (
    { practiceId, productId }: { practiceId: number; productId: number },
    { rejectWithValue }
  ) => {
    try {
      const response = await requestExploreMarketProductAgreement(practiceId, productId);
      return {
        productId,
        data: response?.data ?? response,
      };
    } catch (error) {
      return rejectWithValue(error || "An error occurred while requesting product agreement");
    }
  }
);

// CS-1747 START - Fetch requested product IDs from agreement_request table (replaces userPreference)
export const getRequestedProductIds = createAsyncThunk(
  "exploreMarket/getRequestedProductIds",
  async (_, { rejectWithValue }) => {
    try {
      const productIds = await fetchRequestedProductIds();
      return productIds;
    } catch (error) {
      return rejectWithValue(error || "An error occurred while fetching requested product IDs");
    }
  }
);

// CS-1747: Update agreement request status (admin action)
export const updateAgreementRequestStatusThunk = createAsyncThunk(
  "exploreMarket/updateAgreementRequestStatus",
  async (request: UpdateAgreementRequestStatusRequest, { rejectWithValue }) => {
    try {
      const response = await updateAgreementRequestStatusApi(request);
      return response;
    } catch (error) {
      return rejectWithValue(error || "An error occurred while updating agreement request status");
    }
  }
);
// CS-1747 END

const exploreMarketSlice = createSlice({
  name: "exploreMarket",
  initialState,
  reducers: {
    setFavorites: (state, action: PayloadAction<number[]>) => {
      state.marketData.forEach(p => {
        p.isFavorited = action.payload.includes(p.productId);
      });
    },
    // CS-1731: Set requested agreements from user preferences
    setRequestedAgreements: (state, action: PayloadAction<number[]>) => {
      state.marketData.forEach((p) => {
        p.isAgreementRequestedByUser = action.payload.includes(p.productId);
      });
    },
    // CS-1731: Mark agreement as requested for a specific product
    markAgreementRequestedForProduct: (
      state,
      action: PayloadAction<{ productId: number; requested: boolean }>
    ) => {
      const product = state.marketData.find((p) => p.productId === action.payload.productId);
      if (product) {
        product.isAgreementRequestedByUser = action.payload.requested;
      }
    },
    resetMarketData: (state) => {
      state.marketData = [];
      state.requestedProductIds = [];
      state.fetchStatus = "idle";
    },
    // CS-1731: Reset Search Request Agreement list state
    resetSearchAgreementRequestList: (state) => {
      state.searchAgreementRequestList = {
        content: [],
        totalElements: 0,
        totalPages: 0,
        size: 0,
      };
    }
  },
  extraReducers: (builder) => {
    builder
      .addCase(getExploreMarketProducts.pending, (state) => {
        state.fetchStatus = "loading";
      })
      .addCase(getExploreMarketProducts.fulfilled, (state, action) => {
        state.fetchStatus = "success";
        const apiProducts = action.payload || [];
        state.marketData = apiProducts.map((p: any) => ({
          productId: p.productId,
          productName: p.productName || "",
          hcpcsCode: p.hcpcsCode || null,
          productCategory: p.productCategory || null,
          composition: p.composition || null,
          compoundType: p.compoundType || null,
          allowableRate: p.allowableRate != null ? Number(p.allowableRate) : null,
          listPrice: p.listPrice != null ? Number(p.listPrice) : null,
          mues: p.mues || null,
          manufacturerId: p.manufacturerId || null,
          manufacturerName: p.manufacturerName || "",
          isAgreementRequired: p.isAgreementRequired ?? false,
          agreementStatus: p.agreementStatus || null,
          agreementMode: p.agreementMode || null,
          productLogoId: p.productLogoId || null,
          isFavorited: false,
          isAgreementRequestedByUser: state.requestedProductIds.includes(p.productId),
          productStatus: p.productStatus || "ACTIVE",
          skuAllowablePrices: p.skuAllowablePrices || [],
          macPrices: p.macPrices || [],
          productDescription: p.productDescription || null,
          extendedPrice:p.extendedPrice != null ? Number(p.extendedPrice) : null,
          hasSKUPrice:p.hasSKUPrice,
          purchaseAndBill: p.purchaseAndBill || null, // CS-1765
          productConfigs: p.productConfigs || []
        }));
      })
      .addCase(getExploreMarketProducts.rejected, (state) => {
        state.fetchStatus = "error";
        state.marketData = [];
      })
      .addCase(searchAgreementRequests.pending, (state) => {
        state.searchAgreementRequestStatus = "loading";
        state.searchAgreementRequestError = null;
      })
      .addCase(searchAgreementRequests.fulfilled, (state, action) => {
        state.searchAgreementRequestStatus = "success";
        // state.searchAgreementRequestList = action.payload;
        state.searchAgreementRequestList = action.payload ?? { content: [], totalElements: 0, totalPages: 0, size: 0 }; // CS-1984
      })
      .addCase(searchAgreementRequests.rejected, (state, action) => {
        state.searchAgreementRequestStatus = "error";
        state.searchAgreementRequestError =
          (action.payload as string) ||
          action.error?.message ||
          "Failed to search agreement requests";
      })
      .addCase(requestProductAgreement.pending, (state) => {
        state.requestAgreementLoading = true;
        state.requestAgreementError = null;
      })
      .addCase(requestProductAgreement.fulfilled, (state, action) => {
        state.requestAgreementLoading = false;
        const product = state.marketData.find((p) => p.productId === action.payload.productId);
        if (product) {
          product.isAgreementRequestedByUser = true;
        }
      })
      .addCase(requestProductAgreement.rejected, (state, action) => {
        state.requestAgreementLoading = false;
        state.requestAgreementError =
          (action.payload as string) ||
          action.error?.message ||
          "Failed to request product agreement";
      })
      // CS-1747 START - getRequestedProductIds extraReducers
      .addCase(getRequestedProductIds.fulfilled, (state, action) => {
        const productIds: number[] = action.payload || [];
        state.requestedProductIds = productIds;
        state.marketData.forEach((p) => {
          p.isAgreementRequestedByUser = productIds.includes(p.productId);
        });
      })
      // CS-1747 END
      ;
  },
});

export const {
  setFavorites,
  setRequestedAgreements,
  markAgreementRequestedForProduct,
  resetMarketData,
  resetSearchAgreementRequestList,
} = exploreMarketSlice.actions;

export const useExploreMarketSlice = () =>
  useAppSelector((state) => state.exploreMarket);

export default exploreMarketSlice.reducer;
